import { useState } from "react";

interface MedEntry {
  name: string;
  kind?: string;
  strength?: string;
  dose?: string;
  frequency?: string;
  schedule?: string;
  reason?: string;
}

const KINDS = [
  ["prescription", "Prescription"],
  ["otc", "Over-the-counter"],
  ["vitamin", "Vitamin"],
  ["supplement", "Supplement"],
  ["hormone", "Hormone"],
  ["injection", "Injection"],
  ["pellet", "Pellet"],
  ["inhaler", "Inhaler"],
  ["other", "Other"]
] as const;

export function MedicationsField({
  value,
  onChange
}: {
  value: Record<string, unknown>[];
  onChange: (value: unknown) => void;
}) {
  const meds = value as unknown as MedEntry[];
  const [draft, setDraft] = useState<MedEntry>({ name: "", kind: "prescription", schedule: "scheduled" });

  function add() {
    if (!draft.name.trim()) return;
    onChange([...meds, { ...draft, name: draft.name.trim() }]);
    setDraft({ name: "", kind: "prescription", schedule: "scheduled" });
  }

  function remove(index: number) {
    onChange(meds.filter((_, i) => i !== index));
  }

  return (
    <div className="stack">
      {meds.length > 0 && (
        <div className="table-cards">
          {meds.map((m, i) => (
            <div className="table-card" key={i}>
              <div className="row-between">
                <div>
                  <div style={{ fontWeight: 600 }}>{m.name}</div>
                  <div className="dim text-xs">
                    {[m.kind, m.strength, m.dose, m.frequency].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <button className="btn btn-sm btn-ghost" onClick={() => remove(i)} type="button">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ background: "var(--surface-1)" }}>
        <div className="field">
          <label>Name</label>
          <input
            className="input"
            value={draft.name}
            placeholder="e.g. Testosterone, Vitamin D, Ibuprofen"
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Type</label>
          <select
            className="select"
            value={draft.kind}
            onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
          >
            {KINDS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="row wrap">
          <div className="field grow" style={{ marginBottom: 0 }}>
            <label>Strength</label>
            <input
              className="input"
              value={draft.strength ?? ""}
              placeholder="e.g. 2000 IU"
              onChange={(e) => setDraft({ ...draft, strength: e.target.value })}
            />
          </div>
          <div className="field grow" style={{ marginBottom: 0 }}>
            <label>Frequency</label>
            <input
              className="input"
              value={draft.frequency ?? ""}
              placeholder="e.g. daily, as needed"
              onChange={(e) => setDraft({ ...draft, frequency: e.target.value })}
            />
          </div>
        </div>
        <button className="btn btn-sm mt-4" type="button" onClick={add} disabled={!draft.name.trim()}>
          Add medication
        </button>
      </div>
    </div>
  );
}
